import {getExcaliburAuditData} from "./excaliburAPIs";
import {ASC, DESC} from "../../../common/constants/constants";
import orderBy from 'lodash/orderBy';
import {getLoadedPageCount, mergeRows, needsMoreRows} from "../../../common/pagination/paging";

const TRANSACTION_TYPE = 'Excalibur';

// Backend calls made in a row to fill the pager window after one page change; stops a backend that
// keeps answering with few rows and a cursor from looping.
const MAX_CHAINED_FETCHES = 3;

export const INITIAL_STATE = {
    lastEvaluatedKey: null,
    rows: [],
    pageCount: 1,
    currentPageIndex: 1,
    fetching: false,
    chainedFetches: 0,
    requestToken: 0,
    sortingColumn: {"sortingField": "startTimestamp"},
    sortingDescending: true,
    loading: true,
    loadingText: "Loading requests",
    initialize: true
};

export const tableReducerInit = initialState => {
    return initialState;
}

// Tags every response with the token of the request that asked for it, so a response from before a
// refresh is dropped instead of merged into the new rows.
const fetchPage = (dispatcher, startKey, requestToken) => {
    getExcaliburAuditData(action => dispatcher({
        ...action,
        payload: {...action.payload, requestToken, dispatcher}
    }), startKey, TRANSACTION_TYPE);
}

const sortRows = (rows, state) => orderBy(rows, state.sortingColumn.sortingField, state.sortingDescending ? DESC : ASC);

export const tableReducer = (state, action) => {
    switch (action.type) {
        case "INITIALIZE": {
            if (!state.initialize) {
                return state;
            }
            fetchPage(action.payload.dispatcher, null, state.requestToken);
            return {
                ...state,
                initialize: false,
                fetching: true,
                loading: true
            }
        }
        case "API_RESPONSE": {
            if (action.payload.requestToken !== state.requestToken) {
                return state;
            }
            const rows = sortRows(mergeRows(state.rows, action.payload.data, "transactionId"), state);
            const pageCount = getLoadedPageCount(rows);
            const lastEvaluatedKey = action.payload.lastEvaluatedKey || null;
            let currentPageIndex = state.currentPageIndex;
            const chain = needsMoreRows(currentPageIndex, pageCount, lastEvaluatedKey)
                && state.chainedFetches < MAX_CHAINED_FETCHES;
            if (chain) {
                fetchPage(action.payload.dispatcher, lastEvaluatedKey, state.requestToken);
            } else {
                // The page stepped onto turned out not to exist: back onto the last loaded one.
                currentPageIndex = Math.min(currentPageIndex, pageCount);
            }
            return {
                ...state,
                rows,
                pageCount,
                lastEvaluatedKey,
                currentPageIndex,
                fetching: chain,
                chainedFetches: chain ? state.chainedFetches + 1 : 0,
                loading: chain && currentPageIndex > pageCount,
                initialize: false
            }
        }
        case "PAGE_INDEX_CHANGED": {
            const hasMore = Boolean(state.lastEvaluatedKey);
            let pageIndex = action.payload.pageIndex;
            if (pageIndex > state.pageCount && !hasMore) {
                pageIndex = state.pageCount;
            }
            const startFetch = !state.fetching && needsMoreRows(pageIndex, state.pageCount, hasMore);
            if (startFetch) {
                fetchPage(action.payload.dispatcher, state.lastEvaluatedKey, state.requestToken);
            }
            const fetching = state.fetching || startFetch;
            return {
                ...state,
                currentPageIndex: pageIndex,
                fetching,
                chainedFetches: 0,
                loading: fetching && pageIndex > state.pageCount
            }
        }
        case "SORTING_CHANGED": {
            const sorted = {
                ...state,
                sortingDescending: action.payload.isDescending,
                sortingColumn: {"sortingField": action.payload.sortingField}
            };
            return {
                ...sorted,
                rows: sortRows(state.rows, sorted)
            }
        }
        case "REFRESH": {
            const requestToken = state.requestToken + 1;
            fetchPage(action.payload.dispatcher, null, requestToken);
            return {
                ...INITIAL_STATE,
                sortingColumn: state.sortingColumn,
                sortingDescending: state.sortingDescending,
                requestToken,
                initialize: false,
                fetching: true
            }
        }
        case "FAILURE_API_RESPONSE": {
            if (action.payload.requestToken !== state.requestToken) {
                return state;
            }
            return {
                ...state,
                fetching: false,
                chainedFetches: 0,
                loading: false,
                initialize: false,
                currentPageIndex: Math.min(state.currentPageIndex, state.pageCount)
            }
        }
        default:
            return state;
    }
}
